import { EPST_ADULTS_DISTRIBUTIONS, EPST_KIDS_DISTRIBUTIONS } from "data/certifications/epstDistribution";

/**
 * Desordena aleatoriamente los elementos de un array (algoritmo de Fisher-Yates).
 * @param {Array} array - Array original.
 * @returns {Array} - Nuevo array con los elementos en orden aleatorio.
 */
export const shuffleArray = (array) => {
    const copiedArray = [...array];
    for (let i = copiedArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copiedArray[i], copiedArray[j]] = [copiedArray[j], copiedArray[i]];
    }
    return copiedArray;
};

/**
 * Divide un array en subgrupos del tamaño especificado.
 * @param {Array} array - Array original.
 * @param {number} batchSize - Tamaño de cada grupo.
 * @returns {Array[]} - Array de grupos.
 */
export const groupArray = (array, batchSize) => {
    const grouped = [];
    for (let i = 0; i < array.length; i += batchSize) {
        grouped.push(array.slice(i, i + batchSize));
    }
    return grouped;
};

/**
 * Selecciona un elemento aleatorio de cada grupo dentro de un array de grupos.
 * @param {Array[]} groupedArray - Array de grupos de elementos.
 * @returns {Array} - Elementos aleatorios, uno por grupo.
 */
export const selectRandomFromGroups = (groupedArray) => {
    return groupedArray.map(group => {
        const randomIndex = Math.floor(Math.random() * group.length);
        return group[randomIndex];
    });
};

/**
 * Limita un array al número de elementos especificado.
 * @param {Array} array - Array original.
 * @param {number} limit - Número máximo de elementos permitidos.
 * @returns {Array} - Array limitado.
 */
export const limitArray = (array, limit) => {
    return array.slice(0, limit);
};

/**
 * Ejecuta un proceso completo de agrupación, selección aleatoria por grupo y limitación.
 * @param {Array} questions - Array de preguntas.
 * @param {number} groupSize - Tamaño del grupo para dividir las preguntas.
 * @param {number} limit - Número máximo de preguntas finales.
 * @returns {Array} - Array de preguntas seleccionadas.
 */
export const processQuestions = (questions, groupSize, limit) => {
    const grouped = groupArray(questions, groupSize);
    const randomSelection = selectRandomFromGroups(grouped);
    return limitArray(randomSelection, limit);
};

/**
 * Selecciona preguntas basadas en el nivel y sección según la distribución por tipo de candidato.
 * @param {Array} questions - Lista completa de preguntas.
 * @param {number} sectionId - ID de la sección actual.
 * @param {number} candidate_type - Tipo de candidato (1: Kids, 2: Adults).
 * @returns {Array} - Preguntas seleccionadas con tiempo asignado por nivel.
 */
export const selectQuestionsByLevelAndSection = (questions, sectionId, candidate_type) => {
    const distribution = candidate_type === 1
        ? EPST_KIDS_DISTRIBUTIONS[sectionId]
        : EPST_ADULTS_DISTRIBUTIONS[sectionId];

    if (!distribution) return [];

    const grouped = {};

    // Agrupar preguntas por nivel
    questions.forEach((q) => {
        if (!grouped[q.level_id]) grouped[q.level_id] = [];
        grouped[q.level_id].push(q);
    });

    const selectedQuestions = [];

    // Seleccionar preguntas según distribución
    for (const [levelId, config] of Object.entries(distribution)) {
        const group = grouped[levelId] || [];
        const shuffled = shuffleArray(group).slice(0, config.questions);
        const withTime = shuffled.map(q => ({ ...q, time: config.time }));
        selectedQuestions.push(...withTime);
    }

    return selectedQuestions;
};